import { useCallback, useEffect, useRef, useState } from 'react';
import type { PdpEddProductSpecs } from '../../domain/models/ResolvedExpectedDeliveryDate';
import {
  toPdpDeliveryPresentationVm,
  type PdpDeliveryPresentationVm,
} from '../viewModels/pdpDelivery.vm';

interface PdpDeliveryAppVmResult {
  edd: string | null;
  eddFullDate: string | null;
  sameDayMessage: string | null;
  pincode: string;
  cityName: string | null;
  storeName: string | null;
  contactNumber: string | null;
  whatsappNumber: string | null;
  isDirectContactAllowed: boolean;
  nearbyStoreCount: number;
  inventoryStores: Array<{
    storeId: string;
    storeName: string;
    storeAddress: string;
    contactNumber: string;
    cityName: string;
  }>;
  nearbyStoresList: Array<{
    storeId: string;
    storeName: string;
    storeAddress: string;
    contactNumber: string;
    cityName: string;
  }>;
  designEddDetails: unknown | null;
}

interface PdpDeliveryServiceInterface {
  getPdpDeliveryAppVm(params: {
    pincode: string;
    designId: string;
    customizationId: number;
    productSpecs: PdpEddProductSpecs;
    selectedSize: string | null;
  }): Promise<PdpDeliveryAppVmResult>;
}

export type PdpDeliveryModal = 'nearby' | 'inventory' | null;

export interface UsePdpPincodeDeliveryOptions {
  service: PdpDeliveryServiceInterface;
  pincode: string | undefined;
  designId: string;
  customizationId: number;
  productSpecs: PdpEddProductSpecs;
  selectedSize: string | null;
}

export interface UsePdpPincodeDeliveryReturn {
  delivery: PdpDeliveryPresentationVm;
  isLoading: boolean;
  deliveryError: string | null;
  activeModal: PdpDeliveryModal;
  openNearbyStoresModal: () => void;
  openInventoryModal: () => void;
  closeModal: () => void;
  refetchDelivery: () => void;
}

export function usePdpPincodeDelivery({
  service,
  pincode,
  designId,
  customizationId,
  productSpecs,
  selectedSize,
}: UsePdpPincodeDeliveryOptions): UsePdpPincodeDeliveryReturn {
  const [delivery, setDelivery] = useState<PdpDeliveryPresentationVm>(
    toPdpDeliveryPresentationVm(null)
  );
  const [isLoading, setIsLoading] = useState(false);
  const [deliveryError, setDeliveryError] = useState<string | null>(null);
  const [activeModal, setActiveModal] = useState<PdpDeliveryModal>(null);
  const requestIdRef = useRef(0);

  const fetchDelivery = useCallback(async () => {
    if (!designId) return;

    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setDeliveryError(null);

    const resolvedPincode = pincode && pincode.length === 6 ? pincode : '';

    try {
      const result = await service.getPdpDeliveryAppVm({
        pincode: resolvedPincode,
        designId,
        customizationId,
        productSpecs,
        selectedSize,
      });
      if (requestId !== requestIdRef.current) return;
      setDelivery(toPdpDeliveryPresentationVm(result));
    } catch {
      if (requestId !== requestIdRef.current) return;
      setDelivery(toPdpDeliveryPresentationVm(null));
      setDeliveryError('Unable to fetch delivery details. Please try again.');
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [service, pincode, designId, customizationId, productSpecs, selectedSize]);

  useEffect(() => {
    if (!designId) return;
    void fetchDelivery();
  }, [fetchDelivery, designId]);

  const openNearbyStoresModal = useCallback(() => setActiveModal('nearby'), []);
  const openInventoryModal = useCallback(() => setActiveModal('inventory'), []);
  const closeModal = useCallback(() => setActiveModal(null), []);

  return {
    delivery,
    isLoading,
    deliveryError,
    activeModal,
    openNearbyStoresModal,
    openInventoryModal,
    closeModal,
    refetchDelivery: fetchDelivery,
  };
}
